import { useEffect, useState } from 'react'
import { Sparkles, Loader2, CalendarClock, Send, Repeat, Save, RotateCcw } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { useApi } from '@/hooks/use-api'
import { useGeneratePost, useSchedulePost, usePublishPost, useUpdatePost, useCreateAutoSchedule, useAutoSchedules } from './use-content'
import type { ContentType, ContentTone, Post } from './types'
import { CONTENT_TYPE_LABELS, TONE_LABELS } from './types'
import { DAY_LABELS, formatLocalTime } from '@dealcloser/shared'

/**
 * Generate → review → act (owner request 2026-09-05).
 *
 * The old dialog asked for a schedule BEFORE the post existed. Now the user
 * describes the post, sees what the AI wrote (and can tweak it), and only
 * then picks what happens to it:
 *   - Post now            publishes through the connected LinkedIn account
 *   - Schedule            one-off publish time
 *   - Auto-post campaign  turns this topic/format/tone into a recurring
 *                         schedule; the reviewed post goes out at the first
 *                         slot and the AI writes a fresh one for each later
 *                         slot (Pro can run several, bounded by the plan's
 *                         weekly auto-post allowance)
 *   - Save as draft       keeps it in the list for later
 *
 * 2026-10-03: the auto-post panel now knows the plan's weekly allowance
 * (Basic 1, Pro 7, shared with the Settings poster). It offers frequency
 * presets ("Once a week", "Mon, Wed, Fri", "Every weekday", "Every day",
 * custom days), disables the ones that do not fit, and defaults to one
 * that does. Before, it always defaulted to Mon/Wed/Fri, so every Basic
 * user hit a 403 on their first try. `intent: 'auto'` (the "New auto-post
 * campaign" entry point) opens the panel as soon as the post is generated.
 */

/**
 * Default schedule value for the datetime-local input: 1 hour from now,
 * formatted as `YYYY-MM-DDTHH:mm` in the user's local timezone (datetime-local
 * has no timezone — the browser interprets it as local).
 */
function defaultScheduleValue(): string {
  const d = new Date(Date.now() + 60 * 60 * 1000)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York'
  } catch {
    return 'America/New_York'
  }
}

export type GenerateDialogInitial = {
  readonly topic?: string
  readonly contentType?: ContentType
  readonly tone?: ContentTone
}

type GeneratePostDialogProps = {
  readonly open: boolean
  readonly onClose: () => void
  readonly onGenerated: (post: Post) => void
  /** 'auto' = opened from "New auto-post campaign": pre-select that action after generating. */
  readonly intent?: 'post' | 'auto'
  /** Prefill (e.g. "Regenerate" on an existing draft). */
  readonly initial?: GenerateDialogInitial
}

const CONTENT_TYPES: readonly ContentType[] = [
  'thought_leadership',
  'pain_point',
  'social_proof',
  'case_study',
  'cta',
]

const CONTENT_TONES: readonly ContentTone[] = [
  'professional',
  'conversational',
  'bold',
  'inspirational',
]

type Phase = 'compose' | 'review'
type ReviewAction = 'none' | 'schedule' | 'auto'

type FrequencyPreset = 'weekly' | 'mwf' | 'weekdays' | 'daily' | 'custom'
const PRESET_DAYS: Record<Exclude<FrequencyPreset, 'custom' | 'weekly'>, readonly number[]> = {
  mwf: [1, 3, 5],
  weekdays: [1, 2, 3, 4, 5],
  daily: [0, 1, 2, 3, 4, 5, 6],
}
const PRESET_LABELS: Record<FrequencyPreset, string> = {
  weekly: 'Once a week',
  mwf: 'Mon, Wed, Fri',
  weekdays: 'Every weekday',
  daily: 'Every day',
  custom: 'Custom days',
}

/** Slots a preset needs per week. */
function presetSize(p: FrequencyPreset, customCount: number): number {
  if (p === 'weekly') return 1
  if (p === 'custom') return customCount
  return PRESET_DAYS[p].length
}

/** Largest preset that fits the remaining weekly slots (Mon/Wed/Fri preferred). */
export function defaultPreset(slotsLeft: number): 'mwf' | 'weekly' {
  return slotsLeft >= 3 ? 'mwf' : 'weekly'
}

const HOURS = Array.from({ length: 24 }, (_, h) => h)
const MINUTES = [0, 15, 30, 45]

export function GeneratePostDialog({ open, onClose, onGenerated, intent = 'post', initial }: GeneratePostDialogProps) {
  const apiFetch = useApi()
  const [phase, setPhase] = useState<Phase>('compose')
  const [topic, setTopic] = useState('')
  const [contentType, setContentType] = useState<ContentType>('thought_leadership')
  const [tone, setTone] = useState<ContentTone>('professional')
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  // Review phase
  const [post, setPost] = useState<Post | null>(null)
  const [content, setContent] = useState('')
  const [action, setAction] = useState<ReviewAction>('none')
  const [scheduleAt, setScheduleAt] = useState<string>(defaultScheduleValue())
  const [autoName, setAutoName] = useState('')
  const [autoDays, setAutoDays] = useState<Set<number>>(() => new Set([1, 3, 5]))
  const [autoHour, setAutoHour] = useState(9)
  const [autoMinute, setAutoMinute] = useState(0)
  const [autoApprove, setAutoApprove] = useState(true)
  const [preset, setPreset] = useState<FrequencyPreset>('mwf')
  const [busy, setBusy] = useState<'publish' | 'schedule' | 'auto' | 'draft' | null>(null)

  // Weekly automated-post allowance (plan + Settings poster), for the
  // auto-post panel. Loaded lazily: only when the dialog is open.
  const { data: autoData } = useAutoSchedules(open)
  const allowance = autoData?.weekly_allowance ?? 0
  const slotsLeft = Math.max(0, allowance - (autoData?.weekly_slots_used ?? 0))
  const posterSlots = autoData?.poster_slots ?? 0

  // Prefill on open (Regenerate, deep links).
  useEffect(() => {
    if (!open) return
    if (initial?.topic) setTopic(initial.topic)
    if (initial?.contentType) setContentType(initial.contentType)
    if (initial?.tone) setTone(initial.tone)
  }, [open, initial?.topic, initial?.contentType, initial?.tone])

  function applyPreset(p: FrequencyPreset) {
    setPreset(p)
    if (p === 'weekly') setAutoDays(new Set([[...autoDays][0] ?? 1]))
    else if (p !== 'custom') setAutoDays(new Set(PRESET_DAYS[p]))
  }

  const { mutateAsync: generatePost, isPending: isGenerating } = useGeneratePost()
  const { mutateAsync: schedulePost } = useSchedulePost()
  const { mutateAsync: publishPost } = usePublishPost()
  const { mutateAsync: updatePost } = useUpdatePost()
  const { mutateAsync: createAutoSchedule } = useCreateAutoSchedule()
  const isPending = isGenerating || busy !== null

  function resetAll() {
    setPhase('compose')
    setTopic('')
    setError(null)
    setNotice(null)
    setPost(null)
    setContent('')
    setAction('none')
    setScheduleAt(defaultScheduleValue())
    setAutoName('')
    setAutoDays(new Set([1, 3, 5]))
    setAutoHour(9)
    setAutoMinute(0)
    setAutoApprove(true)
    setPreset('mwf')
    setBusy(null)
  }

  function handleClose() {
    if (isPending) return
    resetAll()
    onClose()
  }

  async function handleGenerate() {
    if (!topic.trim()) {
      setError('Please enter a topic before generating.')
      return
    }
    setError(null)
    try {
      const generated = await generatePost({ topic: topic.trim(), content_type: contentType, tone })
      setPost(generated)
      setContent(generated.content)
      setAutoName(topic.trim().slice(0, 80))
      setPhase('review')
      if (autoData) {
        const p = defaultPreset(slotsLeft)
        setPreset(p)
        setAutoDays(new Set(p === 'weekly' ? [1] : PRESET_DAYS[p]))
      }
      if (intent === 'auto') setAction('auto')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Generation failed. Please try again.')
    }
  }

  /** Persist inline edits before any action so what the user saw is what ships. */
  async function persistEdits(): Promise<Post> {
    if (!post) throw new Error('No post to save')
    if (content.trim() === post.content.trim()) return post
    const updated = await updatePost({ id: post.id, updates: { content: content.trim() } })
    setPost(updated)
    return updated
  }

  function finish(finalPost: Post) {
    onGenerated(finalPost)
    resetAll()
    onClose()
  }

  async function handleSaveDraft() {
    setBusy('draft')
    setError(null)
    try {
      finish(await persistEdits())
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the post.')
    } finally {
      setBusy(null)
    }
  }

  async function handlePublishNow() {
    setBusy('publish')
    setError(null)
    try {
      const saved = await persistEdits()
      const res = await apiFetch<{ status: string; account: { unipile_account_id: string } | null }>(
        '/api/leads/linkedin/status',
      )
      const accountId = res.data?.account?.unipile_account_id
      if (!accountId || res.data?.status !== 'connected') {
        throw new Error('No LinkedIn account connected. Connect your LinkedIn account in Settings first.')
      }
      const published = await publishPost({ id: saved.id, accountId })
      finish(published)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to publish the post.')
    } finally {
      setBusy(null)
    }
  }

  async function handleSchedule() {
    const ts = new Date(scheduleAt).getTime()
    if (Number.isNaN(ts) || ts <= Date.now()) {
      setError('Schedule time must be in the future.')
      return
    }
    setBusy('schedule')
    setError(null)
    try {
      const saved = await persistEdits()
      const scheduled = await schedulePost({ id: saved.id, scheduledAt: new Date(scheduleAt).toISOString() })
      finish(scheduled)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to schedule the post.')
    } finally {
      setBusy(null)
    }
  }

  async function handleCreateAuto() {
    if (autoDays.size === 0) {
      setError('Pick at least one day of the week.')
      return
    }
    setBusy('auto')
    setError(null)
    try {
      const saved = await persistEdits()
      const created = await createAutoSchedule({
        name: autoName.trim() || topic.trim(),
        topic: topic.trim(),
        content_type: contentType,
        tone,
        days_of_week: [...autoDays].sort((a, b) => a - b),
        hour_local: autoHour,
        minute_local: autoMinute,
        timezone: browserTimeZone(),
        auto_approve: autoApprove,
        seed_post_id: saved.id,
      })
      const when = created.seeded_post_at ? new Date(created.seeded_post_at) : null
      setNotice(
        when
          ? `Auto-post campaign created. This post goes out ${when.toLocaleString()} and a fresh one is written for every later slot.`
          : 'Auto-post campaign created. Each slot will produce a draft for you to review.',
      )
      // Give the user a beat to read the confirmation, then close.
      setTimeout(() => finish(saved), 1600)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create the auto-post campaign.')
      setBusy(null)
    }
  }

  function toggleDay(d: number) {
    if (preset === 'weekly') {
      setAutoDays(new Set([d]))
      return
    }
    setPreset('custom')
    setAutoDays((prev) => {
      const next = new Set(prev)
      if (next.has(d)) next.delete(d)
      else next.add(d)
      return next
    })
  }

  const overAllowance = autoData !== undefined && autoDays.size > slotsLeft

  const choiceButton = (selected: boolean) =>
    `rounded-md border px-3 py-2 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ring disabled:pointer-events-none disabled:opacity-50 ${
      selected
        ? 'border-cobalt-bright bg-primary text-primary-foreground'
        : 'border-border bg-background text-foreground hover:bg-accent'
    }`

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className={phase === 'review' ? 'sm:max-w-2xl' : 'sm:max-w-md'}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-cobalt-bright" />
            {phase === 'compose' ? 'Generate LinkedIn Post' : 'Review your post'}
          </DialogTitle>
          <DialogDescription>
            {phase === 'compose'
              ? intent === 'auto'
                ? 'Set the topic, format and tone. You will see a sample post first, then choose how often posts like it go out.'
                : 'Describe your topic and pick a format. You will see the post before deciding when it goes out.'
              : 'Edit anything you like, then post it now, schedule it, or turn it into a recurring auto-post campaign.'}
          </DialogDescription>
        </DialogHeader>

        {phase === 'compose' && (
          <div className="space-y-4 pt-2">
            {/* Topic */}
            <div className="space-y-1.5">
              <Label htmlFor="gen-topic">Topic</Label>
              <Input
                id="gen-topic"
                placeholder="e.g. Why most cold outreach fails"
                value={topic}
                onChange={(e) => {
                  setTopic(e.target.value)
                  if (error) setError(null)
                }}
                onKeyDown={(e) => e.key === 'Enter' && !isPending && void handleGenerate()}
                disabled={isPending}
                maxLength={200}
              />
            </div>

            {/* Content type */}
            <div className="space-y-2">
              <Label>Content Type</Label>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {CONTENT_TYPES.map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setContentType(type)}
                    disabled={isPending}
                    className={choiceButton(contentType === type)}
                  >
                    {CONTENT_TYPE_LABELS[type]}
                  </button>
                ))}
              </div>
            </div>

            {/* Tone */}
            <div className="space-y-2">
              <Label>Tone</Label>
              <div className="grid grid-cols-2 gap-2">
                {CONTENT_TONES.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setTone(t)}
                    disabled={isPending}
                    className={choiceButton(tone === t)}
                  >
                    {TONE_LABELS[t]}
                  </button>
                ))}
              </div>
            </div>

            {error && (
              <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </p>
            )}

            {isGenerating && (
              <div className="flex items-center justify-center gap-2 rounded-md border border-primary/30 bg-primary/10 py-4">
                <Loader2 className="h-4 w-4 animate-spin text-cobalt-bright" />
                <span className="text-sm text-muted-foreground">Generating…</span>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={handleClose} disabled={isPending}>
                Cancel
              </Button>
              <Button onClick={() => void handleGenerate()} disabled={isPending || !topic.trim()} className="gap-2">
                {isGenerating ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Generating…</>
                ) : (
                  <><Sparkles className="h-4 w-4" /> Generate Post</>
                )}
              </Button>
            </div>
          </div>
        )}

        {phase === 'review' && post && (
          <div className="space-y-4 pt-2">
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span className="rounded-full border px-2 py-0.5">{CONTENT_TYPE_LABELS[contentType]}</span>
              <span className="rounded-full border px-2 py-0.5">{TONE_LABELS[tone]}</span>
              <span className="truncate">Topic: {topic}</span>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="gen-content">Post</Label>
              <textarea
                id="gen-content"
                className="min-h-[220px] w-full resize-y rounded-md border bg-transparent p-3 text-sm leading-relaxed outline-none focus:ring-2 focus:ring-ring"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                disabled={isPending}
                maxLength={3000}
              />
              <p className="text-right text-[11px] text-muted-foreground">{content.length}/3000</p>
            </div>

            {/* Action picker */}
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <Button
                variant="default"
                className="justify-start gap-2"
                onClick={() => void handlePublishNow()}
                disabled={isPending || !content.trim()}
              >
                {busy === 'publish' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Post now
              </Button>
              <Button
                variant={action === 'schedule' ? 'secondary' : 'outline'}
                className="justify-start gap-2"
                onClick={() => setAction(action === 'schedule' ? 'none' : 'schedule')}
                disabled={isPending}
              >
                <CalendarClock className="h-4 w-4" />
                Schedule for later
              </Button>
              <Button
                variant={action === 'auto' ? 'secondary' : 'outline'}
                className="justify-start gap-2"
                onClick={() => setAction(action === 'auto' ? 'none' : 'auto')}
                disabled={isPending}
              >
                <Repeat className="h-4 w-4" />
                Auto-post campaign
              </Button>
              <Button
                variant="ghost"
                className="justify-start gap-2"
                onClick={() => void handleSaveDraft()}
                disabled={isPending}
              >
                {busy === 'draft' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save as draft
              </Button>
            </div>

            {action === 'schedule' && (
              <div className="space-y-2 rounded-md border p-3">
                <Label htmlFor="gen-schedule-at">Publish at</Label>
                <div className="flex flex-wrap items-center gap-2">
                  <Input
                    id="gen-schedule-at"
                    type="datetime-local"
                    value={scheduleAt}
                    onChange={(e) => setScheduleAt(e.target.value)}
                    disabled={isPending}
                    className="max-w-xs text-sm"
                  />
                  <Button onClick={() => void handleSchedule()} disabled={isPending} className="gap-2">
                    {busy === 'schedule' ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarClock className="h-4 w-4" />}
                    Confirm schedule
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">Times are in your local timezone ({browserTimeZone()}).</p>
              </div>
            )}

            {action === 'auto' && (
              <div className="space-y-3 rounded-md border p-3">
                <div className="space-y-1">
                  <p className="text-sm font-medium">Post like this on a schedule</p>
                  <p className="text-xs text-muted-foreground">
                    This post goes out at the first slot. For every later slot the AI writes a fresh post on the
                    same topic, format and tone.
                  </p>
                  {autoData && (
                    <p className="text-xs text-muted-foreground" data-testid="auto-allowance">
                      {slotsLeft} of {allowance} automated post{allowance === 1 ? '' : 's'} per week left on your plan
                      {posterSlots > 0 ? ` (the LinkedIn Poster in Settings uses ${posterSlots})` : ''}.
                    </p>
                  )}
                </div>
                {autoData && slotsLeft === 0 && (
                  <p className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-300">
                    {allowance <= 1
                      ? 'Your plan includes 1 automated post per week and it is already in use. Pause your other auto-post campaign or the LinkedIn Poster, or upgrade to Pro for up to 7 per week.'
                      : 'All of your weekly automated posts are in use. Pause another auto-post campaign or turn off the LinkedIn Poster in Settings to free a slot.'}
                  </p>
                )}
                <div className="space-y-1.5">
                  <Label htmlFor="gen-auto-name">Campaign name</Label>
                  <Input
                    id="gen-auto-name"
                    value={autoName}
                    onChange={(e) => setAutoName(e.target.value)}
                    maxLength={80}
                    disabled={isPending}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>How often</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {(['weekly', 'mwf', 'weekdays', 'daily', 'custom'] as const).map((p) => {
                      const needs = presetSize(p, autoDays.size)
                      const fits = !autoData || p === 'custom' || needs <= slotsLeft
                      return (
                        <button
                          key={p}
                          type="button"
                          onClick={() => applyPreset(p)}
                          disabled={isPending || !fits}
                          title={fits ? undefined : `Needs ${needs} weekly slots; ${slotsLeft} left`}
                          className={choiceButton(preset === p)}
                        >
                          {PRESET_LABELS[p]}
                        </button>
                      )
                    })}
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>{preset === 'weekly' ? 'Day' : 'Days'}</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {DAY_LABELS.map((label, d) => (
                      <button
                        key={label}
                        type="button"
                        onClick={() => toggleDay(d)}
                        disabled={isPending}
                        className={choiceButton(autoDays.has(d))}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex flex-wrap items-end gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="gen-auto-hour">Time</Label>
                    <div className="flex items-center gap-1">
                      <select
                        id="gen-auto-hour"
                        className="h-9 rounded-md border bg-transparent px-2 text-sm"
                        value={autoHour}
                        onChange={(e) => setAutoHour(Number(e.target.value))}
                        disabled={isPending}
                      >
                        {HOURS.map((h) => (
                          <option key={h} value={h}>{formatLocalTime(h, 0).replace(':00', '')}</option>
                        ))}
                      </select>
                      <select
                        aria-label="Minute"
                        className="h-9 rounded-md border bg-transparent px-2 text-sm"
                        value={autoMinute}
                        onChange={(e) => setAutoMinute(Number(e.target.value))}
                        disabled={isPending}
                      >
                        {MINUTES.map((m) => (
                          <option key={m} value={m}>:{String(m).padStart(2, '0')}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <p className="pb-2 text-xs text-muted-foreground">{browserTimeZone()}</p>
                </div>
                {overAllowance && slotsLeft > 0 && (
                  <p className="text-xs text-amber-300">
                    Pick {slotsLeft === 1 ? 'one day' : `up to ${slotsLeft} days`} to fit your weekly allowance.
                  </p>
                )}
                <label className="flex cursor-pointer items-start gap-2">
                  <Checkbox
                    checked={autoApprove}
                    onCheckedChange={(v) => setAutoApprove(v === true)}
                    disabled={isPending}
                    className="mt-0.5"
                  />
                  <span className="text-xs">
                    <span className="font-medium">Publish automatically.</span>{' '}
                    <span className="text-muted-foreground">Off: each post waits as a draft for your approval.</span>
                  </span>
                </label>
                <div className="flex justify-end">
                  <Button onClick={() => void handleCreateAuto()} disabled={isPending || autoDays.size === 0 || overAllowance} className="gap-2">
                    {busy === 'auto' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Repeat className="h-4 w-4" />}
                    Create auto-post campaign
                  </Button>
                </div>
              </div>
            )}

            {notice && (
              <p className="rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-400">
                {notice}
              </p>
            )}
            {error && (
              <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </p>
            )}

            <div className="flex items-center justify-between pt-1">
              <Button
                variant="ghost"
                size="sm"
                className="gap-1.5 text-xs"
                onClick={() => {
                  setPhase('compose')
                  setAction('none')
                  setError(null)
                }}
                disabled={isPending}
              >
                <RotateCcw className="h-3.5 w-3.5" /> Change topic &amp; regenerate
              </Button>
              <Button variant="outline" onClick={handleClose} disabled={isPending}>
                Close
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
